import { useCallback, useEffect, useRef, useState } from "react";
import type { Page, WorkspaceMember } from "@sol-office/contracts";
import { DateTime } from "luxon";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { api, errorMessage, RequestError } from "../api";
import { ErrorNotice, initials, Spinner } from "../ui";
import type { Person, Task } from "./types";

export const canEditTask = (task: Task) =>
  !task.archivedAt &&
  !task.deletedAt &&
  ["owner", "editor"].includes(task.role);
export const canCommentTask = (task: Task) =>
  !task.archivedAt && !task.deletedAt && task.role !== "viewer";
export const canManageTask = (task: Task) =>
  ["owner", "editor"].includes(task.role);
export const isUnavailable = (error: unknown) =>
  error instanceof RequestError && [401, 403, 404].includes(error.status);

export function taskDue(task: Task, fallbackZone?: string) {
  const zone =
    task.dueTimeZone ||
    fallbackZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (task.dueDate)
    return DateTime.fromISO(task.dueDate, { zone }).toLocaleString(
      DateTime.DATE_MED,
    );
  if (task.dueAt)
    return DateTime.fromISO(task.dueAt, { zone }).toLocaleString(
      DateTime.DATETIME_MED,
    );
  return "No due date";
}
export function taskUrl(
  workspaceId: string,
  taskId: string | null,
  commentId?: string,
) {
  const params = new URLSearchParams();
  if (location.pathname === `/w/${workspaceId}/tasks`) {
    const current = new URLSearchParams(location.search);
    if (current.get("assignedTo") === "me") params.set("assignedTo", "me");
    const view = current.get("view");
    if (
      view &&
      ["all", "mine", "overdue", "done", "archive", "trash"].includes(view)
    )
      params.set("view", view);
  }
  if (taskId) params.set("task", taskId);
  if (commentId) params.set("comment", commentId);
  return `/w/${workspaceId}/tasks${params.size ? `?${params}` : ""}`;
}
export function readSelection() {
  const params = new URLSearchParams(location.search);
  return { taskId: params.get("task"), commentId: params.get("comment") };
}
export function navigateTask(
  workspaceId: string,
  taskId: string | null,
  commentId?: string,
) {
  if (
    !window.dispatchEvent(
      new Event("sol:before-navigate", { cancelable: true }),
    )
  )
    return false;
  history.pushState({}, "", taskUrl(workspaceId, taskId, commentId));
  window.dispatchEvent(new Event("sol:route-changed"));
  return true;
}

export function useLifetime() {
  const life = useRef({ active: true, epoch: 0 });
  useEffect(() => {
    life.current.active = true;
    return () => {
      life.current.active = false;
      life.current.epoch++;
    };
  }, []);
  return useCallback(() => {
    const epoch = life.current.epoch;
    return () => life.current.active && epoch === life.current.epoch;
  }, []);
}

export function useUnsavedChanges(dirty: boolean, pending = false) {
  const state = useRef({ dirty, pending });
  state.current = { dirty, pending };
  const allowClose = useCallback(() => {
    if (state.current.pending) return false;
    return (
      !state.current.dirty ||
      window.confirm("You have unsaved task changes. Discard them and leave?")
    );
  }, []);
  // A successful save may navigate before React renders the new baseline.
  // Release the guard synchronously only after the server confirms the write.
  const release = useCallback(() => {
    state.current = { dirty: false, pending: false };
  }, []);
  useEffect(() => {
    const before = (event: Event) => {
      if (event.defaultPrevented) return;
      if (!allowClose()) event.preventDefault();
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (state.current.dirty || state.current.pending) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    let currentUrl = location.href;
    const routeChanged = () => {
      currentUrl = location.href;
    };
    const pop = (event: PopStateEvent) => {
      if (!allowClose()) {
        event.stopImmediatePropagation();
        history.pushState({}, "", currentUrl);
        window.dispatchEvent(new Event("sol:route-changed"));
      } else currentUrl = location.href;
    };
    window.addEventListener("sol:before-navigate", before);
    window.addEventListener("beforeunload", unload);
    window.addEventListener("popstate", pop, { capture: true });
    window.addEventListener("sol:route-changed", routeChanged);
    return () => {
      window.removeEventListener("sol:before-navigate", before);
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("popstate", pop, { capture: true });
      window.removeEventListener("sol:route-changed", routeChanged);
    };
  }, [allowClose]);
  return Object.assign(allowClose, { release });
}

export function usePeople(workspaceId: string) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    const abort = new AbortController();
    setPeople([]);
    setLoading(true);
    setError("");
    void (async () => {
      try {
        let cursor: string | null = null;
        const found: Person[] = [];
        do {
          const page: Page<WorkspaceMember> = await api(
            `/workspaces/${workspaceId}/members?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
            { signal: abort.signal },
          );
          if (!active) return;
          found.push(
            ...page.items
              .filter(
                (person) =>
                  person.status === "active" && person.role !== "guest",
              )
              .map((person) => person.user),
          );
          cursor = page.nextCursor;
        } while (cursor);
        if (active) setPeople(found);
      } catch (cause) {
        if (active) setError(errorMessage(cause));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
      abort.abort();
    };
  }, [workspaceId, retry]);
  return {
    people,
    loading,
    error,
    retry: () => setRetry((value) => value + 1),
  };
}

export function PeoplePicker({
  people,
  value,
  onChange,
  label = "Assignees",
  disabled = false,
  multiple = true,
  exclude = [],
  loading = false,
}: {
  people: Person[];
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  exclude?: string[];
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState("");
  const choices = people.filter(
    (person) =>
      !exclude.includes(person.id) &&
      `${person.name} ${person.email}`
        .toLocaleLowerCase()
        .includes(query.toLocaleLowerCase()),
  );
  return (
    <div className="tasks-people-picker">
      <span className="tasks-field-label">{label}</span>
      <button
        type="button"
        className="tasks-people-trigger"
        aria-expanded={open}
        aria-label={`Choose ${label.toLocaleLowerCase()}`}
        disabled={disabled || loading}
        onClick={() => setOpen(!open)}
      >
        <span>
          {value.length
            ? value
                .map(
                  (id) =>
                    people.find((person) => person.id === id)?.name ||
                    "Current member",
                )
                .join(", ")
            : "Choose people…"}
        </span>
        <ChevronDown size={16} />
      </button>
      {open && !disabled && (
        <div className="tasks-people-options">
          <label className="tasks-search">
            <Search size={15} />
            <input
              aria-label={`Search ${label.toLocaleLowerCase()}`}
              placeholder="Find a person…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <div role="group" aria-label={label}>
            {choices.map((person) => (
              <label key={person.id} className="tasks-person-option">
                <input
                  type={multiple ? "checkbox" : "radio"}
                  name={`task-person-${label}`}
                  checked={value.includes(person.id)}
                  onChange={(event) => {
                    onChange(
                      multiple
                        ? event.target.checked
                          ? [...value, person.id]
                          : value.filter((id) => id !== person.id)
                        : [person.id],
                    );
                    if (!multiple) setOpen(false);
                  }}
                />
                <span className="tasks-avatar">{initials(person.name)}</span>
                <span>
                  <strong>{person.name}</strong>
                  <small>{person.email}</small>
                </span>
              </label>
            ))}
            {!choices.length && (
              <p className="tasks-small-note">No matching eligible members.</p>
            )}
          </div>
          {multiple && (
            <button
              type="button"
              className="button secondary compact"
              onClick={() => setOpen(false)}
            >
              <Check size={14} />
              Done
            </button>
          )}
        </div>
      )}
      {loading && <Spinner label="Loading members…" />}
      {!!value.length && !disabled && (
        <button
          type="button"
          className="tasks-inline-button"
          onClick={() => onChange([])}
        >
          Clear selection
        </button>
      )}
    </div>
  );
}

export function LabelsInput({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  function add() {
    const label = draft.trim();
    if (!label) return;
    if (label.length > 60 || value.length >= 20) {
      setError("Use up to 20 labels, with 60 characters each.");
      return;
    }
    if (
      !value.some(
        (item) => item.toLocaleLowerCase() === label.toLocaleLowerCase(),
      )
    )
      onChange([...value, label]);
    setDraft("");
    setError("");
  }
  return (
    <div className="tasks-label-input">
      <span className="tasks-field-label">Labels</span>
      <div className="tasks-labels">
        {value.map((label) => (
          <span className="tasks-label" key={label}>
            {label}
            <button
              type="button"
              aria-label={`Remove label ${label}`}
              disabled={disabled}
              onClick={() => onChange(value.filter((item) => item !== label))}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="tasks-input-action">
        <input
          aria-label="New label"
          value={draft}
          maxLength={60}
          placeholder="Add a label…"
          disabled={disabled || value.length >= 20}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="button secondary compact"
          disabled={disabled || !draft.trim()}
          onClick={add}
        >
          Add
        </button>
      </div>
      {error && <ErrorNotice message={error} />}
    </div>
  );
}
